import { fromScaled, toScaled } from '@colplat/protocol';
import { byId, groupBy, type Ledger, type TransactionRow } from './rows.js';
import {
  descendantIds, normalTransferKind, transferAmounts, transferRoles, UNASSIGNED, UNASSIGNED_SHORT, UNASSIGNED_WALLET,
  walletLegs, debtShiftLegs,
  type TransferRole,
} from './wallets.js';

/**
 * One wallet's money legs in order, with a running total and the shortfall
 * episodes that fall out of it (ADR-0023), computed from rows (ADR-0025).
 *
 * Episodes are computed, never stored. That is the point for the migration:
 * the loan history does not exist in the old data, so the only way to find out
 * when a wallet was living on somebody else's money is to walk its register
 * and watch the running total cross zero.
 */

const MONEY_TYPES: ReadonlySet<string> = new Set(['ASSET', 'LIABILITY']);
/** Only a category makes a useful counterpart — see `counterpartOf`. */
const CATEGORY_TYPES: ReadonlySet<string> = new Set(['EXPENSE', 'INCOME']);

/** One money leg of a row that collapsed several of them. */
export interface RegisterLeg {
  splitId: string;
  walletId: string | null;
  walletName: string;
  /** See `RegisterRow.walletShort`. */
  walletShort: string;
  accountId: string;
  accountName: string;
  amount: string;
  /** See `RegisterRow.accountOverdrawn` — a collapsed row's legs are on different accounts. */
  accountOverdrawn: boolean;
  /** See `RegisterRow.accountBefore`. */
  accountBefore: string | null;
  accountAfter: string | null;
}

export interface RegisterRow {
  splitId: string;
  transactionId: string;
  txDate: string;
  description: string;
  /** The money account this leg is on — what an account filter narrows by. */
  accountId: string;
  accountName: string;
  counterpart: string;
  walletId: string | null;
  walletName: string;
  /**
   * The same wallet, as short as its owner made it — `wallet.shortName`, or
   * the full name when there is none.
   *
   * Both are carried because both are wanted at once: the register notches
   * the short one into the figure's border, where there is room for a few
   * characters, and says the full one aloud, where there is room for all of
   * it. A reader who cannot see the notch should not be the one getting the
   * abbreviation.
   */
  walletShort: string;
  /**
   * What this row did to the running total, signed — `'0'` for a row whose
   * legs cancelled out, which is the whole point of collapsing one.
   *
   * Everything that accumulates reads this field: the running total, the tag
   * total on the client, and the cover test in `computeEpisodes`. Keeping it
   * as the row's *effect* rather than its headline figure is what lets a
   * collapsed row pass through all three untouched.
   */
  amount: string;
  /**
   * The sum that changed hands, unsigned — set only on a collapsed row, and
   * what the display shows there in place of `amount`.
   *
   * Unsigned because neither sign is true. Nothing entered or left the scope
   * being looked at; money moved inside it, and the direction is in
   * `walletName` and in `legs`.
   */
  moved: string | null;
  /**
   * Set only on a row that stands for a DEBT this wallet is party to, in an
   * entry none of whose money passed through it: the debt's amount,
   * unsigned, in `currencyCode`. `amount` is `'0'` on such a row, because
   * the wallet's money did not move.
   *
   * Asked from use: Renovation paid for Maintenance's mattress as a loan,
   * and Maintenance's log had no line for it at all — so with "If debts
   * were settled" on, its running total stepped down by 379,225.52 HUF with
   * nothing on screen to say why.
   */
  debtAmount: string | null;
  /**
   * True when this leg left its ASSET account below zero in that leg's
   * currency — money spent out of an account that did not hold it.
   *
   * A different fact from the running total beside it, and the reason it is
   * here at all: the running total says whether a *wallet* is short, which
   * is about what money is earmarked for. This says whether the *account*
   * is, which is about whether the money exists. A wallet can be perfectly
   * funded out of a bank account that is empty, and the reverse.
   *
   * Counted over every transaction on that account, never only the ones on
   * screen — a bank balance is not smaller for being looked at through one
   * wallet, or through a tag filter.
   *
   * Always false on a LIABILITY: a liability normally IS a credit balance,
   * so a sign test marks the ordinary state of every loan.
   */
  accountOverdrawn: boolean;
  /**
   * The account's balance in this currency before this entry and after it —
   * set where `accountOverdrawn` is, so the mark can say how far it went,
   * not only that it did. `null` elsewhere.
   */
  accountBefore: string | null;
  accountAfter: string | null;
  /**
   * The legs behind this row, when it stands for more than one. Empty on an
   * ordinary row, so a caller can treat a non-empty `legs` as "this is a
   * collapsed row" without a second flag to keep in step.
   */
  legs: RegisterLeg[];
  /**
   * Which name tells the collapsed legs apart — the thing that actually
   * moved.
   *
   * A loan moved an earmark between two wallets, so the wallets differ and
   * the accounts do not. A move between two accounts of one wallet is the
   * other way round, and listing "Unassigned" twice there says nothing. The
   * caller shows whichever this names; `null` on an ordinary row.
   */
  legsDifferBy: 'wallet' | 'account' | null;
  currencyCode: string;
  running: string;
  episodeId: string | null;
  isCover: boolean;
  /** `loan` or `grant` — a stored `repay` reads as `loan` (`normalTransferKind`). */
  transferKind: string | null;
  transferFromWalletId: string | null;
  transferToWalletId: string | null;
  /** On a loan: how much of it paid back a debt, and how much was lent on top. */
  transferRole: TransferRole | null;
  /**
   * This row is the earmark move a debt implies, not a line of the entry
   * (`debtShiftLegs`): the transfer's id. Null on every ordinary row.
   */
  shiftOf: string | null;
  /** The entry's transfer implies such a move — so its own lines need not say "loan" again. */
  transferShifted: boolean;
  tagIds: string[];
}

export interface Episode {
  id: string;
  currencyCode: string;
  fromRow: number;
  toRow: number;
  fromDate: string;
  toDate: string;
  worst: string;
  closed: boolean;
  covers: Array<{ rowIndex: number; fromWalletId: string; amount: string; kind: string }>;
}

/**
 * A contiguous run of rows, in ONE currency, during which the running balance
 * is below zero.
 *
 * Only rows in the episode's own currency belong to it. A wallet can be short
 * in forints while perfectly solvent in euros, so a euro row that merely falls
 * between two forint rows is not part of that gap — tagging it would draw a
 * shortfall across money that had nothing to do with it, and would also let a
 * second currency's gap overwrite the first one's tag on the same row.
 */
export function computeEpisodes(rows: RegisterRow[]): Episode[] {
  const episodes: Episode[] = [];
  const open = new Map<string, Episode>();

  rows.forEach((row, i) => {
    const ccy = row.currencyCode;
    const running = toScaled(row.running);
    let current = open.get(ccy);
    if (running < 0n) {
      if (!current) {
        current = {
          id: `ep${episodes.length}`, currencyCode: ccy, fromRow: i, toRow: i,
          fromDate: row.txDate, toDate: row.txDate, worst: '0', closed: false, covers: [],
        };
        episodes.push(current);
        open.set(ccy, current);
      }
      current.toRow = i;
      current.toDate = row.txDate;
      if (-running > toScaled(current.worst)) current.worst = fromScaled(-running);
    } else if (current) {
      current.toRow = i;
      current.toDate = row.txDate;
      current.closed = true;
      open.delete(ccy);
    }
  });

  for (const ep of episodes) {
    for (let i = ep.fromRow; i <= ep.toRow; i += 1) {
      const row = rows[i]!;
      if (row.currencyCode !== ep.currencyCode) continue;
      row.episodeId = ep.id;
      if ((row.transferKind === 'loan' || row.transferKind === 'grant') && toScaled(row.amount) > 0n) {
        row.isCover = true;
        ep.covers.push({
          rowIndex: i,
          fromWalletId: row.transferFromWalletId ?? '',
          amount: row.amount,
          kind: row.transferKind,
        });
      }
    }
  }
  return episodes;
}

/**
 * The other side of the transaction — what the row is "about".
 *
 * Only a category counts. On a pure transfer both legs sit on the same
 * account, so naming "the other side" there would echo the row's own account
 * back at the reader; the description already says what happened.
 */
function counterpartOf(ledger: Ledger): Map<string, string> {
  const accounts = byId(ledger.accounts());
  const out = new Map<string, string>();
  for (const split of ledger.splits()) {
    const account = accounts.get(split.accountId);
    if (!account || !CATEGORY_TYPES.has(account.type)) continue;
    if (!out.has(split.transactionId)) out.set(split.transactionId, account.name);
  }
  return out;
}

/**
 * Do these legs, taken together, leave the scope exactly as they found it?
 *
 * Three conditions, and each one is load-bearing:
 *
 *  - **More than one leg.** A single leg is an event whatever it sums to.
 *  - **One currency.** The running total is kept per currency, so a group
 *    that nets to zero across two of them has still moved each of them. Rare
 *    to the point of hypothetical, and collapsing it would be wrong, so it
 *    is excluded rather than reasoned about at the point of use.
 *  - **Sums to zero.** Not "is a wallet transfer" — that is a symptom. What
 *    makes one row honest is that the total did not move, and a transfer
 *    with only one leg in scope (looking at Renovation alone) correctly
 *    fails this and stays a signed row of its own.
 */
function cancelsOut(group: readonly { amount: string; currencyCode: string }[]): boolean {
  if (group.length < 2) return false;
  const ccy = group[0]!.currencyCode;
  if (group.some((l) => l.currencyCode !== ccy)) return false;
  return group.reduce((sum, l) => sum + toScaled(l.amount), 0n) === 0n;
}

/**
 * How much changed hands in an entry whose legs cancel out — what a
 * collapsed row reports as "moved".
 *
 * It was the sum of every leg that arrived, which is right while an entry
 * moves money one way: two legs, one in and one out. It double-counts the
 * moment an entry does two things at once. "Revolut → Tota Gopinath" sends
 * 1,000 from Revolut to another account AND, inside Revolut, lends 1,000 of
 * Renovation's money to Maintenance: two arriving legs of 1,000, read as
 * 2,000 moved, where the most that went anywhere was 1,000. Reported from
 * use, beside the Revolut register saying 1,000.
 *
 * So, netted twice — by account and by wallet — and whichever cut moved
 * more is the answer. An account-to-account move inside one wallet nets to
 * nothing by wallet and to the amount by account; a loan inside one account
 * is the other way round; this entry is 1,000 by both. Each cut counts only
 * what arrived on its net, so an earmark that went out and came back on the
 * same account is not money that moved.
 */
function changedHands(legs: readonly { accountId: string; walletId: string | null; amount: string }[]): bigint {
  const arrived = (key: (l: { accountId: string; walletId: string | null }) => string): bigint => {
    const net = new Map<string, bigint>();
    for (const l of legs) net.set(key(l), (net.get(key(l)) ?? 0n) + toScaled(l.amount));
    return [...net.values()].filter((v) => v > 0n).reduce((sum, v) => sum + v, 0n);
  };
  const byAccount = arrived((l) => l.accountId);
  const byWallet = arrived((l) => l.walletId ?? '');
  return byAccount > byWallet ? byAccount : byWallet;
}

/** One wallet, giving to itself — an account-to-account move inside it. */
function sameWallets(out: readonly (string | null)[], into: readonly (string | null)[]): boolean {
  return out.length === 1 && into.length === 1 && out[0] === into[0];
}

export interface RegisterView {
  rows: RegisterRow[];
  episodes: Episode[];
}

/**
 * `walletId` may be a real id, `unassigned`, or `all`.
 *
 * A named wallet includes everything nested inside it, for the same reason
 * the cards roll descendants up — asking about Renovation means asking about
 * Renovation, sub-envelopes and all.
 *
 * Returns `undefined` for a wallet that does not exist, so a caller can tell
 * "no such wallet" from "a wallet with nothing in it".
 */
export function buildRegister(ledger: Ledger, walletId: string): RegisterView | undefined {
  const wallets = ledger.wallets();
  const nameOf = new Map(wallets.map((w) => [w.id, w.name]));
  const shortOf = new Map(wallets.map((w) => [w.id, (w.shortName ?? '').trim() || w.name]));

  let inScope: (id: string | null) => boolean;
  if (walletId === 'all') {
    inScope = () => true;
  } else if (walletId === UNASSIGNED) {
    inScope = (id) => id === null;
  } else {
    if (!nameOf.has(walletId)) return undefined;
    const ids = new Set([walletId, ...descendantIds(wallets, walletId)]);
    inScope = (id) => id !== null && ids.has(id);
  }

  const accounts = byId(ledger.accounts());
  const transactions = byId(ledger.transactions());
  /*
   * One sidecar per transaction is now enforced where it is written
   * (`walletMoveMembers`), but a ledger written before that can hold
   * several, and a `Map` keeps whichever came last. That made the label on
   * a row change with the order rows happened to arrive in — "Loan" one
   * load and "Handed over" the next. Sorted, so at least it is the same
   * answer every time until the transaction is next saved and tidied.
   */
  const transferByTx = new Map([...ledger.walletTransfers()]
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((wt) => [wt.transactionId, wt]));
  const counterpart = counterpartOf(ledger);
  const tagsByTx = groupBy(ledger.transactionTags(), (t) => t.transactionId);
  const amounts = transferAmounts(ledger);
  const roles = transferRoles(amounts);
  const debtsByTx = groupBy(amounts, (a) => a.transactionId);
  const shifted = new Set(debtShiftLegs(ledger).map((l) => l.shiftOf));

  /*
   * EVERY money leg, not just the ones in scope.
   *
   * A wallet's running total counts the legs in its own scope; an account's
   * balance counts all of them, because an account does not know about the
   * filter and a bank balance is not smaller for being looked at through one
   * wallet. The scope test therefore moves into the walk below — one pass
   * fills both, rather than this filter and then a second traversal for the
   * accounts.
   */
  // As the wallets see them: with the moves debts imply (`walletLegs`).
  const legs = walletLegs(ledger).filter((s) => {
    const account = accounts.get(s.accountId);
    return account !== undefined
      && MONEY_TYPES.has(account.type)
      && transactions.has(s.transactionId);
  });

  /*
   * Date, then `seq`, then the TRANSACTION id, and only then the split id.
   *
   * Without the transaction in that sort, two entries sharing a date and a
   * null `seq` — `seq` is nullable, so that is not rare — have their legs
   * ordered by split id alone and interleave. The running total stays correct
   * either way, but the rows read as four unrelated halves rather than two
   * entries, and the client groups a transaction's legs by adjacency.
   */
  legs.sort((a, b) => {
    const ta = transactions.get(a.transactionId)!;
    const tb = transactions.get(b.transactionId)!;
    return ta.txDate.localeCompare(tb.txDate)
      || (ta.seq ?? 0) - (tb.seq ?? 0)
      || ta.id.localeCompare(tb.id)
      || a.id.localeCompare(b.id);
  });

  const walletNameOf = (id: string | null): string =>
    (id === null ? UNASSIGNED_WALLET : (nameOf.get(id) ?? '?'));
  /*
   * Unassigned has no short name to read, because it has no record to read
   * one from — NULL already means "nobody has earmarked this", and inventing
   * a row for it was ruled out when wallets were designed (ADR-0023). So its
   * abbreviation is a constant, and `∅` is what it means: a leg belonging
   * to no wallet, rather than a wallet with a very short name.
   */
  const walletShortOf = (id: string | null): string =>
    (id === null ? UNASSIGNED_SHORT : (shortOf.get(id) ?? '?'));

  const running = new Map<string, bigint>();
  const rows: RegisterRow[] = [];

  /**
   * Each ASSET account's balance per currency, as the walk passes each leg.
   *
   * ASSET only, deliberately. A LIABILITY normally *is* a credit balance —
   * every liability leg in a real book reads "negative" under a sign test,
   * so marking those would be marking the ordinary state of a loan. The
   * anomaly for a liability is the other direction, and it is a different
   * question from "this account has been spent past empty".
   *
   * Per currency, like everything else here: an account holding euros and
   * forints can be empty of one and not the other, and a single number
   * across both is not a fact about either (ADR-0023's rule for wallets,
   * which applies to accounts for the same reason).
   */
  const accountBalance = new Map<string, bigint>();
  const overdrawn = new Set<string>();
  /** For each overdrawn leg, its account's balance before the entry and after it. */
  const accountMoves = new Map<string, { before: string; after: string }>();
  const isAsset = (accountId: string): boolean => accounts.get(accountId)?.type === 'ASSET';

  /**
   * Walk one TRANSACTION past the account balances, and mark the legs on any
   * ASSET account it leaves below zero.
   *
   * The whole entry first, then the question. It was asked after every leg,
   * and the legs of one entry have no order of their own — they were taken
   * in split-id order, which is a UUID's. A conversion that passes money
   * through an account and out again in the same entry (545 GBP into the
   * Safe, the same 545 GBP out as forints) read as a moment at −545 whenever
   * the outgoing leg's id happened to sort first, and the entry wore an `A`
   * for a balance the account never had. Reported from use. A bank does not
   * see an entry halfway through either.
   */
  const applyToAccounts = (
    group: readonly { id: string; accountId: string; currencyCode: string; amount: string }[],
  ): void => {
    const touched = new Set<string>();
    const before = new Map<string, bigint>();
    for (const leg of group) {
      if (!isAsset(leg.accountId)) continue;
      const key = `${leg.accountId}:${leg.currencyCode}`;
      if (!before.has(key)) before.set(key, accountBalance.get(key) ?? 0n);
      accountBalance.set(key, (accountBalance.get(key) ?? 0n) + toScaled(leg.amount));
      touched.add(key);
    }
    for (const leg of group) {
      const key = `${leg.accountId}:${leg.currencyCode}`;
      // Below zero, not at it: decimal strings summed as scaled integers are
      // exact, and an account emptied to the last cent is not overdrawn.
      if (touched.has(key) && accountBalance.get(key)! < 0n) {
        overdrawn.add(leg.id);
        accountMoves.set(leg.id, { before: fromScaled(before.get(key)!), after: fromScaled(accountBalance.get(key)!) });
      }
    }
  };

  for (let i = 0; i < legs.length;) {
    // The sort above already put one transaction's legs together, so a run
    // of neighbours is the whole transaction — no regroup, no second pass.
    let end = i;
    while (end + 1 < legs.length && legs[end + 1]!.transactionId === legs[i]!.transactionId) end += 1;
    const group = legs.slice(i, end + 1);
    i = end + 1;

    /*
     * The account balances move first, for every leg of this transaction,
     * whether or not it is in scope — that is the half of the walk the
     * filter must not touch. The scope test comes after, and decides only
     * whether a row is drawn.
     */
    applyToAccounts(group);

    /*
     * And from here on, only the legs in scope.
     *
     * `group` is now the transaction's WHOLE set of money legs, because the
     * account balances need all of them — so everything that was true of a
     * pre-filtered group has to be said again against this subset.
     * `cancelsOut` especially: whether a transaction moved money inside what
     * is being looked at is a question about what is being looked at, and
     * asking it of every leg would collapse a pair that only cancels
     * elsewhere.
     */
    const shown = group.filter((l) => inScope(l.walletId));
    if (shown.length === 0) {
      /*
       * Nothing of this wallet's money in the entry — but it may still owe,
       * or be owed, because of it: one row per such debt, which moves the
       * running total by nothing (see `RegisterRow.debtAmount`). Only in one
       * wallet's log; across every wallet a debt is between two of them on
       * screen, and money in no wallet owes nobody.
       */
      if (walletId === 'all' || walletId === UNASSIGNED) continue;
      const tx = transactions.get(group[0]!.transactionId)!;
      for (const debt of debtsByTx.get(tx.id) ?? []) {
        const owes = inScope(debt.toWalletId);
        if (owes === inScope(debt.fromWalletId)) continue;
        const side = owes ? debt.toWalletId : debt.fromWalletId;
        const first = group[0]!;
        rows.push({
          splitId: `!debt:${debt.transferId}`,
          transactionId: tx.id,
          txDate: tx.txDate,
          description: tx.description ?? '',
          accountId: first.accountId,
          accountName: accounts.get(first.accountId)?.name ?? '?',
          counterpart: counterpart.get(tx.id) ?? '',
          walletId: side,
          walletName: walletNameOf(side),
          walletShort: walletShortOf(side),
          amount: '0',
          moved: null,
          debtAmount: debt.amount,
          accountOverdrawn: false,
          accountBefore: null,
          accountAfter: null,
          legs: [],
          legsDifferBy: null,
          currencyCode: debt.currencyCode,
          running: fromScaled(running.get(debt.currencyCode) ?? 0n),
          episodeId: null,
          isCover: false,
          transferKind: normalTransferKind(debt.kind),
          transferFromWalletId: debt.fromWalletId,
          transferToWalletId: debt.toWalletId,
          transferRole: roles.get(debt.transferId) ?? null,
          shiftOf: null,
          transferShifted: false,
          tagIds: (tagsByTx.get(tx.id) ?? []).map((t) => t.tagId),
        });
      }
      continue;
    }

    const first = shown[0]!;
    const tx = transactions.get(first.transactionId)!;
    const transfer = transferByTx.get(first.transactionId);
    const base = {
      transactionId: first.transactionId,
      txDate: tx.txDate,
      description: tx.description ?? '',
      accountId: first.accountId,
      accountName: accounts.get(first.accountId)?.name ?? '?',
      counterpart: counterpart.get(first.transactionId) ?? '',
      currencyCode: first.currencyCode,
      episodeId: null,
      isCover: false,
      transferKind: transfer ? normalTransferKind(transfer.kind) : null,
      transferFromWalletId: transfer?.fromWalletId ?? null,
      transferToWalletId: transfer?.toWalletId ?? null,
      transferRole: (transfer && roles.get(transfer.id)) ?? null,
      transferShifted: transfer !== undefined && shifted.has(transfer.id),
      shiftOf: null,
      tagIds: (tagsByTx.get(first.transactionId) ?? []).map((t) => t.tagId),
    };

    /*
     * Not when one of the lines is a debt's money move (`debtShiftLegs`).
     * Borrowing the whole of a payment makes the borrower's two lines cancel
     * — the borrowed money in, the payment out — and folded they read
     * "0.00 moved", which says nothing about either. Unfolded, the loan
     * arrives first (its id sorts first) and the payment takes it.
     */
    if (cancelsOut(shown) && !shown.some((l) => l.shiftOf)) {
      /*
       * Money that moved INSIDE what is being looked at: a loan between two
       * wallets both on screen, or a move between two accounts earmarked to
       * the same wallet. Two rows for that is two half-truths — neither leg
       * is an event on its own, and the pair reads as money leaving and
       * arriving when nothing did either.
       *
       * So it is one row, and `amount` is `'0'` because that is what it did
       * to the running total. The total therefore does not move across it,
       * which is not an omission: it is the fact.
       *
       * It also removes a phantom. `computeEpisodes` walks row by row, so
       * the outgoing leg used to dip the total before the incoming leg
       * brought it back — opening a shortfall episode inside a parent wallet
       * that never happened, and doing so or not depending on which leg
       * sorted first by split id.
       */
      const out = [...new Set(shown.filter((l) => toScaled(l.amount) < 0n).map((l) => l.walletId))];
      const into = [...new Set(shown.filter((l) => toScaled(l.amount) > 0n).map((l) => l.walletId))];
      const moved = changedHands(shown);
      const withinOneWallet = sameWallets(out, into);
      rows.push({
        ...base,
        // The first leg's id, so the row still has the single stable key the
        // marker, the tag filter and the tag total all address it by.
        splitId: first.id,
        walletId: out.length === 1 ? out[0]! : null,
        walletName: withinOneWallet
          ? walletNameOf(out[0] ?? null)
          : `${out.map(walletNameOf).join(', ')} → ${into.map(walletNameOf).join(', ')}`,
        walletShort: withinOneWallet
          ? walletShortOf(out[0] ?? null)
          : `${out.map(walletShortOf).join(', ')} → ${into.map(walletShortOf).join(', ')}`,
        legsDifferBy: withinOneWallet ? 'account' : 'wallet',
        amount: '0',
        moved: fromScaled(moved),
        debtAmount: null,
        // Out before in, so the pair reads the same way round as the arrow
        // in `walletName`. The sort that got them here is by split id, which
        // puts them in whichever order they were created.
        legs: [...shown]
          .sort((a, b) => Number(toScaled(a.amount) > toScaled(b.amount))
            - Number(toScaled(a.amount) < toScaled(b.amount)))
          .map((l) => ({
            splitId: l.id,
            walletId: l.walletId,
            walletName: walletNameOf(l.walletId),
            walletShort: walletShortOf(l.walletId),
            accountId: l.accountId,
            accountName: accounts.get(l.accountId)?.name ?? '?',
            amount: fromScaled(toScaled(l.amount)),
            accountOverdrawn: overdrawn.has(l.id),
            accountBefore: accountMoves.get(l.id)?.before ?? null,
            accountAfter: accountMoves.get(l.id)?.after ?? null,
          })),
        // The row stands for several accounts, so it is marked if any of
        // them went under — the legs beneath say which.
        accountOverdrawn: shown.some((l) => overdrawn.has(l.id)),
        accountBefore: null,
        accountAfter: null,
        running: fromScaled(running.get(first.currencyCode) ?? 0n),
      });
      continue;
    }

    for (const leg of shown) {
      const next = (running.get(leg.currencyCode) ?? 0n) + toScaled(leg.amount);
      running.set(leg.currencyCode, next);
      rows.push({
        ...base,
        splitId: leg.id,
        shiftOf: leg.shiftOf ?? null,
        accountId: leg.accountId,
        accountName: accounts.get(leg.accountId)?.name ?? '?',
        walletId: leg.walletId,
        walletName: walletNameOf(leg.walletId),
        walletShort: walletShortOf(leg.walletId),
        amount: fromScaled(toScaled(leg.amount)),
        moved: null,
        debtAmount: null,
        legs: [],
        legsDifferBy: null,
        currencyCode: leg.currencyCode,
        accountOverdrawn: overdrawn.has(leg.id),
        accountBefore: accountMoves.get(leg.id)?.before ?? null,
        accountAfter: accountMoves.get(leg.id)?.after ?? null,
        running: fromScaled(next),
      });
    }
  }

  // Across every wallet the running total is simply cash on hand; gaps live
  // inside individual wallets, so there is nothing meaningful to compute.
  return { rows, episodes: walletId === 'all' ? [] : computeEpisodes(rows) };
}

/** One wallet's balance in one currency, as it stood after each day it moved. */
export interface HistoryPoint {
  /** `YYYY-MM-DD`. */
  date: string;
  value: string;
}

export interface WalletHistory {
  currencyCode: string;
  points: HistoryPoint[];
}

/**
 * How a wallet's money got to where it is, one series per currency.
 *
 * Per currency and never combined, because the figures are not comparable:
 * this ledger holds a wallet with 1,781 CAD and −7,836,470 HUF in it, and one
 * y-axis across both would draw the forint and a flat line labelled
 * "everything else". The running totals are already kept per currency for the
 * same reason (`buildRegister`), so this is reading that out rather than a
 * second opinion about it.
 *
 * One point per DAY, not per entry. Three payments on a Tuesday are one thing
 * that happened to the wallet, and plotting them as three steps at the same x
 * draws a vertical line nobody can read; the last one is where the day left
 * the balance, which is what a reader of a balance wants.
 */
export function walletHistory(ledger: Ledger, walletId: string): WalletHistory[] {
  const view = buildRegister(ledger, walletId);
  if (!view) return [];
  const byCcy = new Map<string, Map<string, string>>();
  for (const row of view.rows) {
    let days = byCcy.get(row.currencyCode);
    if (!days) { days = new Map(); byCcy.set(row.currencyCode, days); }
    // Rows arrive oldest first, so the last write for a date wins — which is
    // the balance at the end of that day.
    days.set(row.txDate, row.running);
  }
  return [...byCcy.entries()]
    .map(([currencyCode, days]) => ({
      currencyCode,
      points: [...days.entries()]
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([date, value]) => ({ date, value })),
    }))
    .sort((a, b) => a.currencyCode.localeCompare(b.currencyCode));
}

/**
 * The transactions of one day, in `seq` order, after one of them has been
 * dragged onto another.
 *
 * Pure, and here rather than in the view, because it is the part worth
 * testing: the ids a reorder sends are what the day's order becomes, and a
 * wrong permutation is a silent re-ordering of somebody's books.
 *
 * The day is sorted into `seq` order here, which is the order the server
 * wants back. The log draws it reversed, and the move is the same operation
 * in either direction: removing an item and re-inserting it at the target's
 * index commutes with reversing the array, so this does not need to know
 * which way round the entries are being shown.
 *
 * Returns `undefined` when there is nothing to do — the same transaction, or
 * an id that is not on that day — so a caller can tell "no change" from "a
 * new order" without comparing arrays.
 */
export function dayOrderAfterMove(
  transactions: readonly TransactionRow[], date: string, movedId: string, targetId: string,
): string[] | undefined {
  if (movedId === targetId) return undefined;
  /*
   * Built from the ledger's transactions, not from the register's rows.
   *
   * It was built from the rows, which is wrong the moment the log is
   * filtered: a wallet's log, or a tag filter, shows some of a day's
   * entries and not others, so the ids collected were a subset — and the
   * server refuses anything that is not a permutation of the whole day
   * (`not_a_permutation`). Dragging on a filtered log simply failed.
   *
   * The day entire, in `seq` order, is what gets sent. The entries that are
   * not on screen keep their places around the one that moved.
   */
  const day = transactions
    .filter((t) => t.txDate === date)
    .slice()
    .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0) || a.id.localeCompare(b.id))
    .map((t) => t.id);
  const from = day.indexOf(movedId);
  const to = day.indexOf(targetId);
  if (from < 0 || to < 0) return undefined;
  day.splice(to, 0, ...day.splice(from, 1));
  return day;
}

/** One currency of the book, wallet by wallet, as it stood after an entry. */
export interface BookPosition {
  currencyCode: string;
  /** The book's running total in this currency — the `∑∑` figure on the row. */
  total: string;
  /** The same, by wallet id, with `''` for money in no wallet. */
  parts: Record<string, string>;
}

/**
 * Where the whole book stood after each entry: every currency, wallet by
 * wallet. What hovering a `∑∑` running total breaks down.
 *
 * Read off the book's own register rows rather than walked a second time, so
 * the order is the log's order and each currency's total is, by
 * construction, the running figure printed beside it. A collapsed row moved
 * money between wallets without changing any total, so its legs are what
 * move the parts.
 *
 * Keyed by transaction: a row of the log is one entry, however many legs.
 */
export function bookPositions(rows: readonly RegisterRow[]): Map<string, BookPosition[]> {
  const held = new Map<string, Map<string, bigint>>();
  const add = (ccy: string, walletId: string | null, amount: string): void => {
    let byWallet = held.get(ccy);
    if (!byWallet) { byWallet = new Map(); held.set(ccy, byWallet); }
    const key = walletId ?? '';
    byWallet.set(key, (byWallet.get(key) ?? 0n) + toScaled(amount));
  };
  const out = new Map<string, BookPosition[]>();
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i]!;
    if (row.legs.length > 0) {
      for (const leg of row.legs) add(row.currencyCode, leg.walletId, leg.amount);
    } else {
      add(row.currencyCode, row.walletId, row.amount);
    }
    if (rows[i + 1]?.transactionId === row.transactionId) continue;
    out.set(row.transactionId, [...held.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([currencyCode, byWallet]) => {
        let total = 0n;
        const parts: Record<string, string> = {};
        for (const [key, v] of byWallet) { total += v; parts[key] = fromScaled(v); }
        return { currencyCode, total: fromScaled(total), parts };
      }));
  }
  return out;
}
